import { useEffect, useState, useCallback  } from "react";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { RefreshCw } from "lucide-react";
import { Shield, KeyRound } from "lucide-react";
import { apiFetch, ensureAuthOrRedirect,  redirectToLogin, attachExpiryTimer, getToken } from "../utils/auth";

const frosted =
  "border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-md shadow-[0_0_10px_rgba(56,189,248,0.05)]";

function Banner({ type, message, onDismiss }) {
  if (!message) return null;
  return (
    <div
      className={`mb-4 rounded-lg px-4 py-3 text-sm flex justify-between items-center ${
        type === "success"
          ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-200"
          : "bg-red-500/10 border border-red-500/40 text-red-300"
      }`}
    >
      <span>{message}</span>
      <button
        onClick={onDismiss}
        className="ml-3 text-xs underline hover:text-white"
      >
        Dismiss
      </button>
    </div>
  );
}

export default function Profile() {
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);
const [showKey, setShowKey] = useState(false);
  const [user, setUser] = useState(null);
  const [logins, setLogins] = useState([]);
  const [apiInfo, setApiInfo] = useState([]);
  const [expiryWarning, setExpiryWarning] = useState(null);

  const [activeTab, setActiveTab] = useState("subscription");
  const [confirmOpen, setConfirmOpen] = useState(false);
const [regenLoading, setRegenLoading] = useState(false);
const [apiNotice, setApiNotice] = useState(null);
const [apiError, setApiError] = useState(null);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [retypePassword, setRetypePassword] = useState("");
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [mfaPassword, setMfaPassword] = useState("");

  const [banner, setBanner] = useState({ type: null, message: "" });

  const token =
    localStorage.getItem("jwtToken") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("jwtToken") ||
    window.WHITEINTEL_TOKEN ||
    "";

 useEffect(() => {
  fetch("https://whiteintel.io/api/regular/app/v2/user/get_user_information.php", {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => {
      // 🔐 handle auth errors before parsing
      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }
      return res.json();
    })
    .then((data) => {
      setUser(data.user);
      setTwoFAEnabled(data.user["2fa"] === 1);
      setLogins(data.logins || []);
      setApiInfo(data?.api ?? null); // not [] or {}
      if (data.user?.subscription_expires_at) {
        const today = new Date();
        const expiryDate = new Date(data.user.subscription_expires_at);
        const diffDays = Math.ceil(
          (expiryDate - today) / (1000 * 60 * 60 * 24)
        );

        if (diffDays < 0) {
          setExpiryWarning("Your subscription has expired.");
        } else if (diffDays <= 90) {
          setExpiryWarning(`Your subscription expires in ${diffDays} days.`);
        }
      }
    })
    .catch((err) => {
      if (!err.message.startsWith("AUTH_REDIRECT_")) {
        setBanner({
          type: "error",
          message: "Failed to load profile data.",
        });
      }
    });
}, []);


  const handleUpgrade = () => {
    try {
      window.location.href = "/upgrades";
    } catch {
      window.open("mailto:sales@whiteintel.io", "_blank");
    }
  };

const handleRegenerateKey = useCallback(async () => {
  try {
    setRegenLoading(true);
    setApiNotice(null);
    setApiError(null);

    const res = await fetch(
      "https://whiteintel.io/api/regular/app/v2/apikeys/regenerate_api.php",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      }
    );

    const data = await res.json(); // { apikey: "newapikey" } or { error: "..." }
    if (!res.ok || !data?.apikey) {
      throw new Error(data?.error || "Failed to regenerate key");
    }

    // update apiInfo in place
    setApiInfo((prev) => ({ ...(prev || {}), api_key: data.apikey }));
    setShowKey(false);
    setApiNotice("API key regenerated successfully.");
  } catch (e) {
    setApiError(e.message || "Failed to regenerate key");
  } finally {
    setRegenLoading(false);
    setConfirmOpen(false);
  }
}, []);

  const handlePasswordUpdate = async () => {
    if (!currentPassword || !newPassword || !retypePassword) {
      setBanner({ type: "error", message: "All fields are required." });
      return;
    }
    if (newPassword !== retypePassword) {
      setBanner({ type: "error", message: "New passwords do not match." });
      return;
    }

    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/user/update_password.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            password: currentPassword,
            newPassword,
            newPasswordReType: retypePassword,
          }),
        }
      );
      const data = await res.json();

      if ([401, 419, 440].includes(res.status)) {
      redirectToLogin();
      // throw to stop callers from continuing
      throw new Error(`AUTH_REDIRECT_${res.status}`);
    }
      if (res.ok) {
        setBanner({ type: "success", message: "Password updated successfully." });
        setCurrentPassword("");
        setNewPassword("");
        setRetypePassword("");
      } else {
        setBanner({ type: "error", message: data?.error || "Failed to update password." });
      }
    } catch {
      setBanner({ type: "error", message: "Network error while updating password." });
    }
  };

  const handleToggle2FA = async () => {
    if (!mfaPassword) {
      setBanner({
        type: "error",
        message: "Enter your current password to change 2FA.",
      });
      return;
    }

    try {
      const action = twoFAEnabled ? "disable" : "enable";
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/user/mfa_handler.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            password: mfaPassword,
            action,
          }),
        }
      );
      const data = await res.json();

        if ([401, 419, 440].includes(res.status)) {
      redirectToLogin();
      // throw to stop callers from continuing
      throw new Error(`AUTH_REDIRECT_${res.status}`);
    }

      if (res.ok) {
        setTwoFAEnabled(!twoFAEnabled);
        setBanner({
          type: "success",
          message: `Two-Factor Authentication ${action}d successfully.`,
        });
      } else {
        setBanner({ type: "error", message: data?.error || "Failed to update 2FA." });
      }
    } catch {
      setBanner({ type: "error", message: "Network error while updating 2FA." });
    }
  };

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      <header
        className="bg-transparent px-6 py-4 transition-all duration-300"
        style={{ marginLeft: collapsed ? "72px" : "240px" }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <h1 className="text-[24px] font-bold leading-tight">Profile</h1>
          </div>
        </div>
      </header>

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="watchlists"
          onChange={() => {}}
        />

        <main className="flex-1 px-4 md:px-6">

          {/* Banner for feedback */}
          {banner.message && (
            <Banner
              type={banner.type}
              message={banner.message}
              onDismiss={() => setBanner({ type: null, message: "" })}
            />
          )}

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6">
            {/* LEFT COLUMN */}
            <div className="space-y-6">
              {/* Update Password */}
              <div className={`rounded-2xl ${frosted} p-5`}>
                <div className="flex items-center gap-2 mb-4 border-b border-neutral-800 pb-3">
                  <KeyRound className="h-5 w-5 text-sky-300" />
                  <h2 className="text-lg font-semibold">Update Password</h2>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block mb-1 text-sm text-neutral-300">
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/50 px-3 py-2 text-sm outline-none"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm text-neutral-300">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/50 px-3 py-2 text-sm outline-none"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm text-neutral-300">
                      Retype New Password
                    </label>
                    <input
                      type="password"
                      value={retypePassword}
                      onChange={(e) => setRetypePassword(e.target.value)}
                      className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/50 px-3 py-2 text-sm outline-none"
                    />
                  </div>
                  <button
                    onClick={handlePasswordUpdate}
                    className="rounded-lg border border-sky-600/40 bg-sky-500/10 px-4 py-2 text-sm text-sky-200 hover:bg-sky-500/20"
                  >
                    Update Password
                  </button>
                </div>
              </div>

              {/* Two-Factor Authentication */}
{/* Two-Factor Authentication */}
<div className={`rounded-2xl ${frosted} p-5`}>
  <div className="flex items-center gap-2 mb-4 border-b border-neutral-800 pb-3">
    <Shield className="h-5 w-5 text-sky-300" />
    <h2 className="text-lg font-semibold">Two-Factor Authentication</h2>
  </div>
  <div className="space-y-3">
    <p className="text-sm text-neutral-400">
      Add an extra layer of security to your account.
    </p>

    <div>
      <label className="block mb-1 text-sm text-neutral-300">
        Password
      </label>
      <input
        type="password"
        value={mfaPassword}
        onChange={(e) => setMfaPassword(e.target.value)}
        className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/50 px-3 py-2 text-sm outline-none"
      />
    </div>

    <button
      onClick={handleToggle2FA}
      className={`rounded-lg px-4 py-2 text-sm ${
        twoFAEnabled
          ? "border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
          : "border border-sky-600/40 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
      }`}
    >
      {twoFAEnabled ? "Disable 2FA" : "Enable 2FA"}
    </button>
  </div>
</div>

            </div>

            {/* RIGHT COLUMN: Subscription + Logins */}
            <div className={`rounded-2xl ${frosted} p-5 h-[66vh] flex flex-col`}>
              <div className="flex gap-4 border-b border-neutral-800 pb-2 mb-4">
                <button
                  className={`pb-2 text-sm font-medium ${
                    activeTab === "subscription"
                      ? "text-white border-b-2 border-white"
                      : "text-neutral-400 hover:text-neutral-200"
                  }`}
                  onClick={() => setActiveTab("subscription")}
                >
                  Subscription Details
                </button>
                <button
                  className={`pb-2 text-sm font-medium ${
                    activeTab === "logins"
                      ? "text-white border-b-2 border-white"
                      : "text-neutral-400 hover:text-neutral-200"
                  }`}
                  onClick={() => setActiveTab("logins")}
                >
                  Login History
                </button>

                                <button
                  className={`pb-2 text-sm font-medium ${
                    activeTab === "api"
                      ? "text-white border-b-2 border-white"
                      : "text-neutral-400 hover:text-neutral-200"
                  }`}
                  onClick={() => setActiveTab("api")}
                >
                  API Key
                </button>
              </div>

              {activeTab === "subscription" && user && (
                <div className="space-y-3 text-sm flex-1 overflow-auto">
                  <p><span className="text-neutral-400">Email:</span> {user.email}</p>
                  <p>
                    <span className="text-neutral-400">Organization:</span> {user.organization_name}
                  </p>
                  <p><span className="text-neutral-400">Subscription:</span> {user.subscription}</p>
                  <p><span className="text-neutral-400">Expires at:</span> {user.subscription_expires_at}</p>
                  {expiryWarning && (
                    <p className="mt-2 text-red-400 text-sm">{expiryWarning}</p>
                  )}
                </div>
              )}

              {activeTab === "logins" && (
                <div className="flex-1 overflow-auto scrollbar-thin scrollbar-thumb-neutral-700/70 scrollbar-track-transparent">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-[#0a1224]/90 backdrop-blur-sm">
                      <tr className="text-neutral-400 border-b border-sky-900/30">
                        <th className="px-2 py-2 text-left font-semibold">Date</th>
                        <th className="px-2 py-2 text-left font-semibold">IP</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-800/80">
                      {logins.map((log, idx) => (
                        <tr key={idx} className="hover:bg-white/5">
                          <td className="px-2 py-2">{log.login_date}</td>
                          <td className="px-2 py-2">{log.ip}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}


{activeTab === "api" && (
  apiInfo ? (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-xl rounded-2xl border border-neutral-700/50 bg-[#0a1224]/70 p-5 shadow-sm">
        {/* Inheritance notice */}
        {apiInfo.inherited_from_organization && (
          <div className="mb-3 rounded-md bg-amber-500/10 border border-amber-600/40 px-3 py-2 text-xs text-amber-400">
            This API key is inherited from your organization:{" "}
            <span className="font-medium">{user?.organization_name || "Your Organization"}</span>
          </div>
        )}

        {/* Success / Error inline messages */}
        {apiNotice && (
          <div className="mb-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
            {apiNotice}
          </div>
        )}
        {apiError && (
          <div className="mb-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
            {apiError}
          </div>
        )}

        {/* Key row */}
        <div className="flex items-center justify-between bg-neutral-900/50 rounded-lg px-4 py-3">
          <span className="font-mono text-sm tracking-wider text-neutral-300 select-none break-all">
            {showKey ? (apiInfo?.api_key || "—") : (apiInfo?.api_key?.replace(/.(?=.{4})/g, "•") || "—")}
          </span>

          <div className="flex items-center gap-2 ml-4">
            <button
              onClick={() => setShowKey(!showKey)}
              className="rounded-md px-2 py-1 text-xs text-neutral-400 hover:text-white hover:bg-white/10 transition"
            >
              {showKey ? "Hide" : "Show"}
            </button>
            <button
              onClick={() => navigator.clipboard.writeText(apiInfo.api_key)}
              className="rounded-md px-2 py-1 text-xs text-neutral-400 hover:text-white hover:bg-white/10 transition"
            >
              Copy
            </button>
          </div>
        </div>

        {/* Owner-only actions (not inherited) */}
        {!apiInfo.inherited_from_organization ? (
          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs text-neutral-500">
              Rotating will immediately invalidate the old key. Update all integrations.
            </p>
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={regenLoading}
              className="inline-flex items-center gap-2 rounded-lg border border-sky-600/40 bg-sky-500/20 hover:bg-sky-500/30 px-3 py-2 text-xs text-sky-300 transition disabled:opacity-60"
              title="Regenerate API Key"
            >
              <RefreshCw className={`h-4 w-4 ${regenLoading ? "animate-spin" : ""}`} />
              {regenLoading ? "Regenerating…" : "Regenerate API Key"}
            </button>
          </div>
        ) : (
          <p className="mt-3 text-xs text-neutral-500">
            Keep this key secure. Only organization admins can rotate this key.
          </p>
        )}
      </div>

      {/* Tiny confirm modal */}
      {confirmOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={() => setConfirmOpen(false)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative z-[101] w-full max-w-md rounded-2xl border border-sky-900/40 bg-[#0a1224]/90 p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold">Regenerate API Key?</h3>
            <p className="mt-2 text-sm text-neutral-300">
              This will immediately replace the current key. Clients using the old key will stop working until updated.
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmOpen(false)}
                className="rounded-lg border border-neutral-700 bg-transparent px-3 py-2 text-sm text-neutral-300 hover:bg-white/5"
                disabled={regenLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleRegenerateKey}
                disabled={regenLoading}
                className="inline-flex items-center gap-2 rounded-lg border border-sky-600/40 bg-sky-500/20 px-3 py-2 text-sm text-sky-200 hover:bg-sky-500/30 disabled:opacity-60"
              >
                <RefreshCw className={`h-4 w-4 ${regenLoading ? "animate-spin" : ""}`} />
                {regenLoading ? "Regenerating…" : "Regenerate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  ) : (
    // Fallback when there's no API info for this account
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-xl rounded-2xl border border-amber-600/40 bg-amber-500/10 p-5 shadow-sm">
        <div className="flex items-start gap-3 text-amber-100">
          {/* If you already import AlertTriangle from lucide-react, you can use it here */}
          {/* <AlertTriangle className="h-5 w-5 text-amber-300 mt-0.5" /> */}
          <div>
            <div className="font-semibold">No API key available</div>
            <p className="text-sm opacity-90 mt-1">
              No API key is available for your account. Upgrade your license to enable API access.
            </p>
            <div className="mt-3">
              <button
                onClick={handleUpgrade}
                className="rounded-md border border-amber-500/40 bg-transparent px-3 py-1.5 text-sm hover:bg-amber-500/10"
              >
                Upgrade plan
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
)}



            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
